/**
 * 知识抽查系统 - 会议列表页面
 * 展示所有已结束的会议
 */
import React, { useState, useEffect } from 'react';
import { Card, Button, Tag, message, Empty, Spin } from 'antd';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftOutlined, EyeOutlined, CalendarOutlined } from '@ant-design/icons';
import knowledgeLotteryApi from '../services/knowledgeLotteryApi';

const KnowledgeLotterySessionList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [completedSessions, setCompletedSessions] = useState([]);

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const sessionsResponse = await knowledgeLotteryApi.sessionApi.getAllSessions();
      
      if (sessionsResponse.success) {
        if (sessionsResponse.data && sessionsResponse.data.length > 0) {
          // 筛选已结束的会议（根据会议日期判断）
          const completed = sessionsResponse.data
            .filter(s => {
              if (!s.sessionDate) return false;
              const sessionDate = new Date(s.sessionDate);
              const now = new Date();
              sessionDate.setHours(0, 0, 0, 0);
              now.setHours(0, 0, 0, 0);
              return now > sessionDate; // 晚于会议日期 → 已完成
            })
            .sort((a, b) => {
              const dateA = new Date(a.sessionDate || a.createdAt || 0);
              const dateB = new Date(b.sessionDate || b.createdAt || 0);
              return dateB - dateA; // 降序，最新的在前
            });
          setCompletedSessions(completed);
        } else {
          setCompletedSessions([]);
        }
      } else {
        message.error(sessionsResponse.message || '获取会议列表失败');
        setCompletedSessions([]);
      }
    } catch (error) {
      console.error('获取会议列表失败:', error);
      message.error(error.message || '获取会议列表失败，请检查网络连接');
      setCompletedSessions([]);
    }
    setLoading(false);
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}年${month}月${day}日`;
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5' }}>
      {/* Header */}
      <header style={{ 
        borderBottom: '4px solid #ff4d4f', 
        background: '#fff',
        padding: '24px 0'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Button 
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/knowledge-lottery')}
                style={{ border: 'none' }}
              >
                返回
              </Button>
              <h1 style={{ fontSize: '32px', fontWeight: 'bold', margin: 0 }}>
                会议列表
              </h1>
            </div>
          </div>
        </div>
      </header>

      <main style={{ maxWidth: '1200px', margin: '0 auto', padding: '48px 24px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '100px 0' }}>
            <Spin size="large" />
          </div>
        ) : completedSessions.length > 0 ? (
          <section>
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '24px' }}>
              已结束的会议 ({completedSessions.length} 个)
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {completedSessions.map((session, index) => (
                <Card
                  key={session.id}
                  hoverable
                  onClick={() => navigate(`/knowledge-lottery/session/${session.id}`)}
                  style={{
                    border: '2px solid #d9d9d9',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.3s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#52c41a';
                    e.currentTarget.style.transform = 'translateX(8px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#d9d9d9';
                    e.currentTarget.style.transform = 'translateX(0)';
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                        <div style={{ 
                          width: '32px', 
                          height: '32px', 
                          borderRadius: '50%', 
                          background: '#52c41a',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 'bold',
                          fontSize: '14px'
                        }}>
                          {completedSessions.length - index}
                        </div>
                        <h3 style={{ fontSize: '18px', fontWeight: 'bold', margin: 0 }}>
                          {session.title}
                        </h3>
                        <Tag color="success">已结束</Tag>
                      </div>
                      <div style={{ fontSize: '12px', color: '#999', fontFamily: 'monospace', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <CalendarOutlined /> {formatDate(session.sessionDate)}
                      </div>
                      {session.description && (
                        <div style={{ 
                          fontSize: '14px', 
                          color: '#666',
                          lineHeight: '1.6',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}>
                          {session.description}
                        </div>
                      )}
                    </div>
                    <Button 
                      type="link"
                      icon={<EyeOutlined />}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/knowledge-lottery/session/${session.id}`);
                      }}
                    >
                      查看详情
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </section>
        ) : (
          <Empty
            description="暂无已结束的会议"
            style={{ marginTop: '100px' }}
          >
            <Button 
              type="primary"
              onClick={() => navigate('/knowledge-lottery/manage')}
            >
              前往管理后台创建会议
            </Button>
          </Empty>
        )}
      </main>
    </div>
  );
};

export default KnowledgeLotterySessionList;
